import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Chip,
  Button
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VisibilityIcon from '@mui/icons-material/Visibility';

import EmptyState from '../common/EmptyState';
import JobStatusBadge from './JobStatusBadge';
import JobDetailsModal from '../connectors/JobDetailsModal';
import axios from 'axios';
import { cronToHumanReadable, cronToShortDescription } from '../../utils/cronUtils';

export default function JobsTable({
  schedules,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onToggleSchedule,
  onTriggerSync,
  onDeleteSchedule,
  onEditSchedule,
  sortBy = 'status',
  sortDir = 'asc',
  onSortChange
}) {
  const [jobDetailsOpen, setJobDetailsOpen] = React.useState(false);
  const [selectedJobId, setSelectedJobId] = React.useState(null);

  const handleStatusSort = () => {
    if (onSortChange) {
      onSortChange('status', sortDir === 'asc' ? 'desc' : 'asc');
    }
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '-';
    
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const formatNextRun = (timestamp, minutesUntil) => {
    if (!timestamp) return '-';
    
    const date = new Date(timestamp);
    const now = new Date();
    
    // Format the full date and time
    const dateStr = date.toLocaleDateString();
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    // Add relative time info if available
    let relativeInfo = '';
    if (minutesUntil !== null && minutesUntil !== undefined) {
      if (minutesUntil < 0) {
        relativeInfo = ' (Overdue)';
      } else if (minutesUntil < 60) {
        relativeInfo = ` (${minutesUntil}m)`;
      } else if (minutesUntil < 1440) {
        relativeInfo = ` (${Math.floor(minutesUntil / 60)}h)`;
      } else {
        relativeInfo = ` (${Math.floor(minutesUntil / 1440)}d)`;
      }
    }
    
    return `${dateStr} ${timeStr}${relativeInfo}`;
  };

  const handleViewLastJob = async (schedule) => {
    // Find the most recent job ID for this connector
    try {
      // Get connector ID from either property name
      const connectorId = schedule.connector_id || schedule['s.connector_id'];
      console.log('=== VIEW LOGS CLICKED ===');
      console.log('Schedule object:', schedule);
      console.log('Extracted connector ID:', connectorId);
      
      if (!connectorId) {
        console.error('No connector ID found in schedule:', schedule);
        alert('Connector ID not found');
        return;
      }
      
      // Use axios instead of fetch for consistency with rest of app
      console.log('Making API call to:', `/api/connectors/${connectorId}/sync-history?limit=1`);
      const response = await axios.get(`/api/connectors/${connectorId}/sync-history?limit=1`);
      console.log('Job history API full response:', response);
      console.log('Job history API response data:', response.data);
      console.log('Response data success:', response.data?.success);
      console.log('Response data jobs:', response.data?.jobs);
      console.log('Jobs array length:', response.data?.jobs?.length);
      
      if (response.data.success && response.data.jobs && response.data.jobs.length > 0) {
        const firstJob = response.data.jobs[0];
        console.log('First job from response:', firstJob);
        const jobId = firstJob.id;
        console.log('Extracted job ID:', jobId, 'type:', typeof jobId);
        
        if (!jobId) {
          console.error('Job ID is null/undefined in first job:', firstJob);
          alert('Job ID not found in job data');
          return;
        }
        
        console.log('Setting selectedJobId to:', jobId);
        setSelectedJobId(jobId);
        console.log('Opening job details modal');
        setJobDetailsOpen(true);
      } else {
        console.log('No job history found or empty response');
        console.log('Response success:', response.data?.success);
        console.log('Response has jobs array:', !!response.data?.jobs);
        console.log('Jobs array:', response.data?.jobs);
        alert('No job history found for this connector.');
      }
    } catch (error) {
      console.error('=== VIEW LOGS ERROR ===');
      console.error('Error fetching job history:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      alert(`Failed to load job history: ${error.response?.data?.message || error.message}`);
    }
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Connector</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Platform</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell sortDirection={sortBy === 'status' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'status'}
                  direction={sortBy === 'status' ? sortDir : 'asc'}
                  onClick={handleStatusSort}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Next Run</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {schedules.map((schedule) => (
              <TableRow key={schedule.schedule_id} hover>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="600">
                      {schedule.connector_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {schedule.sync_window_days} day sync window
                      {Number(schedule.intraday_window_days) > 0 && ` · ${Number(schedule.intraday_window_days)}d intraday`}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '10px' }}>
                      ID: {schedule.connector_id || schedule['s.connector_id']} | Schedule: {schedule.schedule_id}
                    </Typography>
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {schedule.brand_name || '-'}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Chip 
                    label={schedule.platform_name} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="500" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      {cronToHumanReadable(schedule.cron_expression || '0 2 * * *')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: '10px' }}>
                      {schedule.cron_expression || '0 2 * * *'}
                    </Typography>
                    {schedule.is_enabled ? (
                      <Chip label="Active" color="success" size="small" sx={{ mt: 0.5 }} />
                    ) : (
                      <Chip label="Paused" color="default" size="small" sx={{ mt: 0.5 }} />
                    )}
                  </Box>
                </TableCell>
                
                <TableCell>
                  <JobStatusBadge status={schedule.last_job_status} />
                </TableCell>
                
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {formatRelativeTime(schedule.last_run_time)}
                    </Typography>
                    {schedule.last_records_processed && parseInt(schedule.last_records_processed) > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {parseInt(schedule.last_records_processed).toLocaleString()} records
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {formatNextRun(schedule.next_scheduled, schedule.next_run_in_minutes)}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={schedule.is_enabled ? "Pause Schedule" : "Resume Schedule"}>
                      <IconButton 
                        size="small" 
                        onClick={() => onToggleSchedule(schedule)}
                      >
                        {schedule.is_enabled ? (
                          <PauseIcon fontSize="small" />
                        ) : (
                          <PlayArrowIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Run Now">
                      <IconButton 
                        size="small" 
                        onClick={() => onTriggerSync(schedule)}
                        color="primary"
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {schedule.last_job_status && (
                      <Tooltip title="View Last Job Logs">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewLastJob(schedule)}
                          color="info"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    <Tooltip title="Edit Schedule">
                      <IconButton 
                        size="small" 
                        onClick={() => onEditSchedule(schedule)}
                        color="secondary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title="Delete Schedule">
                      <IconButton 
                        size="small" 
                        onClick={() => onDeleteSchedule(schedule)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            
            {schedules.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 2, border: 0 }}>
                  <EmptyState
                    compact
                    icon={ScheduleIcon}
                    title="No scheduled jobs found"
                    description="Create your first schedule to automate data syncing"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {pagination.total > 0 && (
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={(_, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        />
      )}
      
      {/* Job Details Modal */}
      <JobDetailsModal
        open={jobDetailsOpen}
        onClose={() => {
          setJobDetailsOpen(false);
          setSelectedJobId(null);
        }}
        jobId={selectedJobId}
        onActionComplete={() => {
          // Optionally refresh the schedules list
          // onRefresh?.();
        }}
      />
    </>
  );
}
